import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatDate, useNavigateToLogout } from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DDLDataTable } from "../DDLDataTable";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { IWSIBMaximumEarnings } from "../../models/WSIBMaximumEarnings";
import { WSIBMaximumEarningsService } from "../../services/WSIBMaximumEarningsService";
import { WSIBMaximumEarningsSetupToolbar } from "./WSIBMaximumEarningsSetupToolbar";
import { WSIBMaximumEarningsSetupValidationSchema } from "./validator";
import { WSIBMaximumEarningsnfoSetupFieldsPanel } from "./Panels/WSIBMaximumEarningsInfoSetupFieldsPanel";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { yupResolver } from "@hookform/resolvers/yup";

// import { SingleColumnLayout } from "../EmployeeAssignemnt/SingleColumnLayout";




export interface IWSIBMaximumEarningsSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "wsibMaximumEarnings-single-data-record";
const WSIB_CATEGORY_LIST_DATA_QUERY_KEY = "wsibMaximumEarnings-list";

const useGetData = (
	clientId: string,
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, id, navigateToLogout],
		queryFn: () => {
			return WSIBMaximumEarningsService.getById(clientId, id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useWSIBMaximumEarnings = (
	runGetListAPICall: boolean,

	provinceId: string,

	effectiveDate: String | null,
	endDate: string | null,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [WSIB_CATEGORY_LIST_DATA_QUERY_KEY, params],
		queryFn: (data) => {

			return WSIBMaximumEarningsService.getWSIBMaximumEarnings(
				runGetListAPICall,

				provinceId,

				effectiveDate,
				endDate,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});


export const WSIBMaximumEarningsSetup: React.FC<IWSIBMaximumEarningsSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [wsibMaximumEarningsId, setWSIBMaximumEarningsId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();
	const [isLoading, setIsLoading] = useState(false);

	const [editRecordId, setEditRecordId] = useState(id);

	// const [runGetListAPICall, setRunGetListAPICall] =
	// 	useState<any>(true);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		// setRunGetListAPICall(true)
	}, []);


	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		clientId,
		editRecordId,
		navigateToLogout
	);

	const DEFAULT_VALUES = {
		provinceId: undefined,
		effectiveDate: null,
		endDate: null,
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(WSIBMaximumEarningsSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});


	const { isPending, data } = useWSIBMaximumEarnings(
		true,
		"",
		"",
		"",
		queryParams,
		navigateToLogout
	);

	const wsibMaximumEarningsList: any | null = data;


	const { isPending: isSubmitting, mutate: submitWSIBMaximumEarningsFormFields } =
		useMutation({
			mutationFn: WSIBMaximumEarningsService.create,
			onSuccess: (res: IWSIBMaximumEarnings | IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIWSIBMaximumEarningsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/workplace-safety/maximum-earnings/setup/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [WSIB_CATEGORY_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {
					// if (instanceOfIWSIBMaximumEarningsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/workplace-safety/maximum-earnings/setup/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [WSIB_CATEGORY_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});




	const upadtedWSIBMaximumEarningsListItems = useMemo(() => {
		return (
			wsibMaximumEarningsList &&
			wsibMaximumEarningsList.items &&
			wsibMaximumEarningsList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/workplace-safety/maximum-earnings/setup/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.province}
					</Link>
				),
				// // shortName: (item?.shortName),
				// label: (item?.province)
			}))
		);
	}, [wsibMaximumEarningsList]);



	const onSubmit = (values: any) => {

		let payload = {
			id: id && id,
			navigateToLogout: navigateToLogout,
			provinceId: values.provinceId,
			earnings: values.earnings,
			effectiveDate: formatDate(values.effectiveDate),
			endDate: formatDate(values.endDate),
		};
		submitWSIBMaximumEarningsFormFields(payload);
	};

	useEffect(() => {
		if (id && selectedDataRecord) {
			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "effectiveDate" && fieldValue) {
					let date = new Date(fieldValue)
					formMethods.setValue(
						fieldName,
						date
					);
				}
				if (fieldName === "endDate" && fieldValue) {
					let date = new Date(fieldValue)
					formMethods.setValue(
						fieldName,
						date
					);
				}
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteWSIBMaximumEarningsById } = useMutation({
		mutationFn: WSIBMaximumEarningsService.delWSIBMaximumEarnings,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the WSIBMaximumEarnings fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/workplace-safety/maximum-earnings/setup`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [WSIB_CATEGORY_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteWSIBMaximumEarningsField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				wsibMaximumEarningsId: wsibMaximumEarningsId,
				navigateToLogout: navigateToLogout,
			};
			deleteWSIBMaximumEarningsById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setWSIBMaximumEarningsId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setWSIBMaximumEarningsId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveWSIBMaximumEarningsById } = useMutation({
		mutationFn: WSIBMaximumEarningsService.archiveWSIBMaximumEarnings,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the WSIBMaximumEarnings fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/workplace-safety/maximum-earnings/setup`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [WSIB_CATEGORY_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const archiveWSIBMaximumEarningsField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				wsibMaximumEarningsId: wsibMaximumEarningsId,
				navigateToLogout: navigateToLogout,
			};
			archiveWSIBMaximumEarningsById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setWSIBMaximumEarningsId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		formMethods.setValue('provinceId', undefined)
		let path = `/workplace-safety/maximum-earnings/setup`;
		navigate(path);
	};

	const expandedLabels = ["Workplace Safety", "Maximum Earnings"];
	const expandedWSIBMaximumEarningsSetupOrSetupItem = "Setup"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Maximum Earnings" &&
				item.label === expandedWSIBMaximumEarningsSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === "Maximum Earnings" && item.label === expandedWSIBMaximumEarningsSetupOrSetupItem && "/workplace-safety/maximum-earnings/setup",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		wsibMaximumEarningsName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onRowEditClicked = useCallback((params: IWSIBMaximumEarnings | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/workplace-safety/maximum-earnings/setup/${params.value}`;
		navigate(path);
	}, []);

	return (
		<Grid
		// sx={{
		// 	pb: "5rem",
		// 	"& .MuiAppBar-root": {
		// 		zIndex: 2,
		// 		top: subMenuVisibility ? "154px" : "112px",
		// 	},
		// }}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isArchive || isLoading}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteWSIBMaximumEarningsField}
				cancelActionHandler={onCloseHandler}
				testId="wsibMaximumEarnings-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="WSIB Maximum Earnings"
										testId={`wsib-maximum-earnings`}
										// leftSpacing={1}
										// rightSpacing={1}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<WSIBMaximumEarningsnfoSetupFieldsPanel setIsLoading={(value: boolean) => setIsLoading(value)} />
									</StandardPanel>
								}
								toolbar={
									<WSIBMaximumEarningsSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 3.5, lg: 3.5, xl: 3.5 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }}
							sx={{ marginTop: "16px" }}
						>
							<DDLDataTable
								rows={
									wsibMaximumEarningsList && upadtedWSIBMaximumEarningsListItems
										? upadtedWSIBMaximumEarningsListItems
										: []
								}
								columns={[
									{ field: "label", headerName: "Province" },
									{ field: "effectiveDate", headerName: "Effective Date" },
									{ field: "endDate", headerName: "End Date" }
								]}
								showHeaderColumns={true}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								showArchiveIcon={false}
								isLoading={isPending}
								totalRecords={
									wsibMaximumEarningsList && wsibMaximumEarningsList.total
										? wsibMaximumEarningsList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveWSIBMaximumEarningsField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"wsibMaximumEarnings-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
